import React, { useMemo } from 'react';

const LineChart = ({
    data = [],
    labelKey = 'date',
    valueKey = 'value',
    height = 200,
    lineColor = '#3B82F6',
    fillColor = 'rgba(59, 130, 246, 0.1)',
    showDots = true,
    showArea = true,
    showGrid = true,
    showLabels = true,
    className = ''
}) => {
    const chartData = useMemo(() => {
        if (!data.length) return { points: [], path: '', areaPath: '', minValue: 0, maxValue: 100 };

        const values = data.map(d => d[valueKey] || 0);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const range = maxValue - minValue || 1;

        const padding = 40;
        const chartWidth = 100; // percentage
        const chartHeight = height - padding;

        const points = data.map((item, index) => {
            const x = (index / (data.length - 1 || 1)) * 100;
            const y = chartHeight - ((item[valueKey] - minValue) / range) * chartHeight + 20;
            return { x, y, value: item[valueKey], label: item[labelKey] };
        });

        // Create SVG path
        const pathPoints = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

        // Create area path
        const areaPath = points.length > 0
            ? `${pathPoints} L ${points[points.length - 1].x} ${chartHeight + 20} L ${points[0].x} ${chartHeight + 20} Z`
            : '';

        return { points, path: pathPoints, areaPath, minValue, maxValue };
    }, [data, valueKey, height]);

    if (!data.length) {
        return (
            <div className={`flex items-center justify-center text-gray-400 ${className}`} style={{ height }}>
                No data available
            </div>
        );
    }

    return (
        <div className={`relative ${className}`} style={{ height }}>
            <svg
                viewBox={`0 0 100 ${height}`}
                preserveAspectRatio="none"
                className="w-full h-full"
            >
                {/* Grid lines */}
                {showGrid && (
                    <g className="text-gray-200">
                        {[0, 25, 50, 75, 100].map(y => (
                            <line
                                key={y}
                                x1="0"
                                y1={20 + (height - 40) * (y / 100)}
                                x2="100"
                                y2={20 + (height - 40) * (y / 100)}
                                stroke="currentColor"
                                strokeWidth="0.2"
                                strokeDasharray="2,2"
                            />
                        ))}
                    </g>
                )}

                {/* Area fill */}
                {showArea && chartData.areaPath && (
                    <path
                        d={chartData.areaPath}
                        fill={fillColor}
                        className="transition-all duration-500"
                    />
                )}

                {/* Line */}
                {chartData.path && (
                    <path
                        d={chartData.path}
                        fill="none"
                        stroke={lineColor}
                        strokeWidth="0.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="transition-all duration-500"
                    />
                )}

                {/* Dots */}
                {showDots && chartData.points.map((point, index) => (
                    <g key={index}>
                        <circle
                            cx={point.x}
                            cy={point.y}
                            r="1"
                            fill={lineColor}
                            className="transition-all duration-300"
                        />
                        {/* Hover area */}
                        <circle
                            cx={point.x}
                            cy={point.y}
                            r="3"
                            fill="transparent"
                            className="cursor-pointer"
                        >
                            <title>{`${point.label}: ${point.value}`}</title>
                        </circle>
                    </g>
                ))}
            </svg>

            {/* X-axis labels */}
            {showLabels && data.length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 px-1">
                    <span>{data[0][labelKey]}</span>
                    {data.length > 2 && (
                        <span>{data[Math.floor(data.length / 2)][labelKey]}</span>
                    )}
                    <span>{data[data.length - 1][labelKey]}</span>
                </div>
            )}

            {/* Y-axis labels */}
            {showLabels && (
                <div className="absolute top-5 left-0 bottom-5 flex flex-col justify-between text-xs text-gray-400">
                    <span>{Math.round(chartData.maxValue)}</span>
                    <span>{Math.round(chartData.minValue)}</span>
                </div>
            )}
        </div>
    );
};

export default LineChart;
